import {
  ApiError as ApiErrorSchema,
  Area as AreaSchema,
  Bootstrap as BootstrapSchema,
  NotificationSchedule as NotificationScheduleSchema,
  Project as ProjectSchema,
  ReviewView as ReviewViewSchema,
  SearchResult as SearchResultSchema,
  Settings as SettingsSchema,
  Space as SpaceSchema,
  TaskPage as TaskPageSchema,
  TaskWithFlags as TaskWithFlagsSchema,
  TodayView as TodayViewSchema,
  UpcomingView as UpcomingViewSchema,
  type Area,
  type Bootstrap,
  type CreateTaskInput,
  type Destination,
  type IconAsset,
  type CloudStatus,
  type McpAccess,
  type SharingState,
  type Media,
  type Project,
  type Settings,
  type Space,
  type Task,
  type TaskPage,
  type TaskQuery,
  type TaskWithFlags,
  type TodayView,
  type UpdateTaskInput,
} from "@desk/contracts";
import type { z } from "zod";
import type { Session } from "./session";

export type ApiErrorCode = z.infer<typeof ApiErrorSchema>["error"]["code"] | "network" | "unknown";

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly current: unknown;
  readonly details: unknown;
  readonly candidates: { id: string; name: string; kind: string }[] | undefined;
  constructor(opts: {
    status: number;
    code: ApiErrorCode;
    message: string;
    current?: unknown;
    details?: unknown;
    candidates?: { id: string; name: string; kind: string }[];
  }) {
    super(opts.message);
    this.name = "ApiRequestError";
    this.status = opts.status;
    this.code = opts.code;
    this.current = opts.current;
    this.details = opts.details;
    this.candidates = opts.candidates;
  }
  /** Network failure, timeout, 5xx, or rate limit: safe to retry later. */
  get retryable(): boolean {
    return this.code === "network" || this.status >= 500 || this.status === 429;
  }
}

/** Human message for an error code; never exposes tokens or internals. */
export function describeError(err: unknown): string {
  if (err instanceof ApiRequestError) {
    switch (err.code) {
      case "network":
        return "Can't reach the Desk service.";
      case "unauthorized":
        return "The saved token was rejected. Pair this device again.";
      case "forbidden":
        return "This token isn't allowed to do that.";
      case "not_found":
        return "That item no longer exists.";
      case "validation_failed":
        return err.message || "Some values aren't valid.";
      case "invalid_parent":
        return err.message || "That destination isn't valid for this item.";
      case "conflict":
        return "This changed somewhere else since you opened it.";
      case "ambiguous":
        return "More than one item matches that name.";
      case "prefix_taken":
        return "That prefix is already used.";
      case "rate_limited":
        return "Too many requests. Trying again shortly.";
      default:
        return err.message || "Something went wrong.";
    }
  }
  return err instanceof Error ? err.message : "Something went wrong.";
}

type Query = Record<string, string | number | boolean | string[] | undefined | null>;

/** Ancestors can move while a task write is queued. The server derives them from container identity. */
function taskDestinationBody<T extends Destination>(input: T) {
  const body = { ...input };
  if (body.projectId) { delete body.areaId; delete body.spaceId; }
  else if (body.spaceId) delete body.areaId;
  return body;
}

function toSearch(query?: Query): string {
  if (!query) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) v.forEach((x) => p.append(k, x));
    else p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** Dev-only schema check: warn on drift, never block the UI on it. */
function check<T>(schema: z.ZodType<T> | undefined, data: unknown, label: string): T {
  if (import.meta.env.DEV && schema) {
    const r = schema.safeParse(data);
    if (!r.success) console.warn(`[desk] response drift for ${label}`, r.error.issues.slice(0, 5));
  }
  return data as T;
}

function arrayOf<T>(data: unknown, ...keys: string[]): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === "object") {
    for (const k of keys) {
      const v = (data as Record<string, unknown>)[k];
      if (Array.isArray(v)) return v as T[];
    }
  }
  return [];
}

function taskOf(data: unknown): TaskWithFlags {
  if (data && typeof data === "object" && "task" in data && (data as { task?: unknown }).task) {
    return (data as { task: TaskWithFlags }).task;
  }
  return data as TaskWithFlags;
}

export interface RequestOptions {
  body?: unknown;
  query?: Query;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export interface SettledView {
  spaces: Space[];
  projects: Project[];
}

/** Description, color, links, and header image of an area or space. */
export type DetailsPatch = Partial<Pick<Area, "description" | "color" | "links" | "headerMediaId">>;
/** Icon fields an area or space accepts: an imported logo, or a preset symbol and color. */
export type IconPatch = Partial<Pick<Area, "iconAssetId" | "icon" | "iconColor">>;

export type ImportSummary = import("@desk/contracts").ImportResult;

export class ApiClient {
  constructor(private session: Session) {}

  get apiUrl() {
    return this.session.apiUrl;
  }
  get token() {
    return this.session.token;
  }

  async request<T = unknown>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      Authorization: `Bearer ${this.session.token}`,
    };
    if (opts.body !== undefined) headers["Content-Type"] = "application/json";
    if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;
    let res: Response;
    try {
      res = await fetch(`${this.session.apiUrl}${path}${toSearch(opts.query)}`, {
        method,
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        signal: opts.signal,
      });
    } catch (e) {
      if ((e as Error)?.name === "AbortError") throw e;
      throw new ApiRequestError({ status: 0, code: "network", message: "Network request failed" });
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    let data: unknown = undefined;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }
    if (!res.ok) {
      const parsed = ApiErrorSchema.safeParse(data);
      if (parsed.success) {
        const e = parsed.data.error;
        throw new ApiRequestError({
          status: res.status,
          code: e.code,
          message: e.message,
          current: e.current,
          details: e.details,
          candidates: e.candidates,
        });
      }
      const code: ApiErrorCode =
        res.status === 401 ? "unauthorized" : res.status === 404 ? "not_found" : res.status === 409 ? "conflict" : "unknown";
      throw new ApiRequestError({ status: res.status, code, message: `HTTP ${res.status}` });
    }
    return data as T;
  }

  // ---------- reads ----------
  health() {
    return this.request<{ ok?: boolean }>("GET", "/health");
  }
  async bootstrap(): Promise<Bootstrap> {
    return check(BootstrapSchema, await this.request("GET", "/v1/bootstrap"), "bootstrap");
  }
  async listTasks(query: TaskQuery, signal?: AbortSignal): Promise<TaskPage> {
    const data = await this.request<unknown>("GET", "/v1/tasks", { query: query as Query, signal });
    if (Array.isArray(data)) return { items: data as TaskWithFlags[], nextCursor: null };
    return check(TaskPageSchema, data, "tasks");
  }
  /** Follows cursors (500 per page, the API maximum) up to `max` items. */
  async listAllTasks(query: TaskQuery, max = Infinity, signal?: AbortSignal): Promise<TaskWithFlags[]> {
    const out: TaskWithFlags[] = [];
    let cursor: string | undefined;
    do {
      const page = await this.listTasks({ ...query, limit: 500, cursor }, signal);
      out.push(...page.items);
      cursor = page.nextCursor ?? undefined;
    } while (cursor && out.length < max);
    return out;
  }
  async getTask(idOrReadable: string): Promise<TaskWithFlags> {
    return check(TaskWithFlagsSchema, taskOf(await this.request("GET", `/v1/tasks/${encodeURIComponent(idOrReadable)}`)), "task");
  }
  async today(date: string, tz: string): Promise<TodayView> {
    return check(TodayViewSchema, await this.request("GET", "/v1/today", { query: { date, tz } }), "today");
  }
  async upcoming(from: string, days = 14) {
    return check(UpcomingViewSchema, await this.request("GET", "/v1/upcoming", { query: { from, days } }), "upcoming");
  }
  async review() {
    return check(ReviewViewSchema, await this.request("GET", "/v1/review"), "review");
  }
  async snoozed(): Promise<TaskWithFlags[]> {
    return arrayOf<TaskWithFlags>(await this.request("GET", "/v1/snoozed"), "items", "tasks");
  }
  async settled(): Promise<SettledView> {
    const data = (await this.request<Record<string, unknown>>("GET", "/v1/settled")) ?? {};
    return { spaces: arrayOf<Space>(data.spaces), projects: arrayOf<Project>(data.projects) };
  }
  async search(q: string, signal?: AbortSignal) {
    const data = await this.request("GET", "/v1/search", { query: { q }, signal });
    return check(SearchResultSchema, data, "search");
  }
  async settings(): Promise<Settings> {
    return check(SettingsSchema, await this.request("GET", "/v1/settings"), "settings");
  }
  async areas(includeArchived = false): Promise<Area[]> {
    return arrayOf<Area>(await this.request("GET", "/v1/areas", { query: { includeArchived } }), "items", "areas").map((a) =>
      check(AreaSchema, a, "area"),
    );
  }
  async spaces(includeArchived = false): Promise<Space[]> {
    return arrayOf<Space>(await this.request("GET", "/v1/spaces", { query: { includeArchived } }), "items", "spaces").map((s) =>
      check(SpaceSchema, s, "space"),
    );
  }
  async projects(includeArchived = false): Promise<Project[]> {
    return arrayOf<Project>(await this.request("GET", "/v1/projects", { query: { includeArchived } }), "items", "projects").map(
      (p) => check(ProjectSchema, p, "project"),
    );
  }
  async notificationSchedule(device: string) {
    return check(
      NotificationScheduleSchema,
      await this.request("GET", "/v1/notifications/schedule", { query: { device, horizonHours: 24 } }),
      "schedule",
    );
  }
  ackNotification(id: string) {
    return this.request("POST", `/v1/notifications/${encodeURIComponent(id)}/ack`, { body: {} });
  }

  // ---------- task writes (normally called through the outbox) ----------
  async createTask(input: CreateTaskInput, idempotencyKey: string): Promise<TaskWithFlags> {
    return taskOf(await this.request("POST", "/v1/tasks", { body: taskDestinationBody(input), idempotencyKey }));
  }
  async updateTask(id: string, patch: UpdateTaskInput, idempotencyKey?: string): Promise<TaskWithFlags> {
    return taskOf(await this.request("PATCH", `/v1/tasks/${id}`, { body: patch, idempotencyKey }));
  }
  async moveTask(id: string, dest: Destination & { baseRevision?: number }, idempotencyKey?: string) {
    return taskOf(await this.request("POST", `/v1/tasks/${id}/move`, { body: taskDestinationBody(dest), idempotencyKey }));
  }
  async taskAction(id: string, action: TaskAction, idempotencyKey?: string): Promise<TaskWithFlags | undefined> {
    if (action === "delete") {
      const r = await this.request("DELETE", `/v1/tasks/${id}`, { idempotencyKey });
      return r ? taskOf(r) : undefined;
    }
    return taskOf(await this.request("POST", `/v1/tasks/${id}/${action}`, { body: {}, idempotencyKey }));
  }
  async snoozeTask(id: string, until: string, idempotencyKey?: string) {
    return taskOf(await this.request("POST", `/v1/tasks/${id}/snooze`, { body: { until }, idempotencyKey }));
  }
  reorderTasks(ids: string[]) {
    return this.request("POST", "/v1/tasks/reorder", { body: { ids } });
  }
  async checklistAdd(taskId: string, title: string, clientId: string) {
    return taskOf(await this.request("POST", `/v1/tasks/${taskId}/checklist`, { body: { title, clientId }, idempotencyKey: clientId }));
  }
  async checklistUpdate(taskId: string, itemId: string, patch: { title?: string; done?: boolean }) {
    return taskOf(await this.request("PATCH", `/v1/tasks/${taskId}/checklist/${itemId}`, { body: patch }));
  }
  async checklistDelete(taskId: string, itemId: string) {
    const r = await this.request("DELETE", `/v1/tasks/${taskId}/checklist/${itemId}`);
    return r ? taskOf(r) : undefined;
  }
  async checklistReorder(taskId: string, ids: string[]) {
    const r = await this.request("POST", `/v1/tasks/${taskId}/checklist/reorder`, { body: { ids } });
    return r ? taskOf(r) : undefined;
  }

  // ---------- planning ----------
  planAdd(date: string, taskIds: string[], idempotencyKey?: string) {
    return this.request("POST", "/v1/plan", { body: { date, taskIds }, idempotencyKey });
  }
  planRemove(date: string, taskId: string, idempotencyKey?: string) {
    return this.request("POST", "/v1/plan/remove", { body: { date, taskId }, idempotencyKey });
  }
  planReorder(date: string, taskIds: string[]) {
    return this.request("POST", "/v1/plan/reorder", { body: { date, taskIds } });
  }
  planMode(date: string, mode: "priority" | "manual") {
    return this.request("PUT", "/v1/plan/mode", { body: { date, mode } });
  }
  carryover(date: string, taskId: string, action: "keep" | "reschedule" | "remove", toDate?: string) {
    return this.request("POST", "/v1/plan/carryover", { body: { date, taskId, action, toDate } });
  }

  // ---------- icons ----------
  /** Store an SVG icon. Identical bytes return the existing asset. */
  createIconAsset(input: { name: string; svg: string; tile?: "auto" | "never" }) {
    return this.request<IconAsset>("POST", "/v1/assets", { body: input });
  }

  // ---------- media ----------
  /** Store an image file (SVG, PNG, JPEG, WebP). Identical bytes return the existing record. */
  createMedia(input: { name: string; mime: Media["mime"]; base64: string }) {
    return this.request<Media>("POST", "/v1/media", { body: input });
  }
  /** An icon rasterized by the server (embedded images included), e.g. for color sampling. */
  iconPng(id: string, theme: "light" | "dark", size: number): Promise<Blob> {
    return this.blob(`/v1/assets/${encodeURIComponent(id)}/png?theme=${theme}&size=${size}`);
  }
  /** The file's bytes; needs the bearer token, so it can't be a plain <img src>. */
  mediaBlob(id: string): Promise<Blob> {
    return this.blob(`/v1/media/${encodeURIComponent(id)}`);
  }
  private async blob(path: string): Promise<Blob> {
    const res = await fetch(`${this.session.apiUrl}${path}`, {
      headers: { Authorization: `Bearer ${this.session.token}` },
    });
    if (!res.ok) throw new ApiRequestError({ status: res.status, code: res.status === 404 ? "not_found" : "unknown", message: `HTTP ${res.status}` });
    return res.blob();
  }

  // ---------- containers ----------
  createArea(name: string, clientId: string) {
    return this.request<Area>("POST", "/v1/areas", { body: { name, clientId }, idempotencyKey: clientId });
  }
  updateArea(id: string, patch: { name?: string; prefix?: string; position?: number } & IconPatch & DetailsPatch) {
    return this.request<Area>("PATCH", `/v1/areas/${id}`, { body: patch });
  }
  createSpace(areaId: string, name: string, clientId: string) {
    return this.request<Space>("POST", "/v1/spaces", { body: { areaId, name, clientId }, idempotencyKey: clientId });
  }
  /** `areaId` moves the space into an area, or to the top level with null; its projects and tasks follow. */
  updateSpace(id: string, patch: { name?: string; position?: number; areaId?: string | null } & IconPatch & DetailsPatch) {
    return this.request<Space>("PATCH", `/v1/spaces/${id}`, { body: patch });
  }
  createProject(spaceId: string, name: string, clientId: string) {
    return this.request<Project>("POST", "/v1/projects", { body: { spaceId, name, clientId }, idempotencyKey: clientId });
  }
  updateProject(
    id: string,
    patch: { name?: string; state?: "planned" | "active"; deadline?: string | null; position?: number; baseRevision?: number } & Partial<
      Pick<Project, "description" | "color" | "links">
    >,
  ) {
    return this.request<Project>("PATCH", `/v1/projects/${id}`, { body: patch });
  }
  completeProject(id: string, openTasks: "complete" | "move_to_space" | "keep") {
    return this.request<Project>("POST", `/v1/projects/${id}/complete`, { body: { openTasks } });
  }
  containerAction(
    kind: "area" | "space" | "project",
    id: string,
    action: "settle" | "unsettle" | "unsnooze" | "archive" | "restore" | "reopen",
  ) {
    return this.request("POST", `/v1/${kind === "area" ? "areas" : kind === "space" ? "spaces" : "projects"}/${id}/${action}`, {
      body: {},
    });
  }
  snoozeContainer(kind: "space" | "project", id: string, until: string) {
    return this.request("POST", `/v1/${kind === "space" ? "spaces" : "projects"}/${id}/snooze`, { body: { until } });
  }

  // ---------- settings & data ----------
  updateSettings(patch: Record<string, unknown>) {
    return this.request<Settings>("PATCH", "/v1/settings", { body: patch });
  }
  exportData() {
    return this.request<unknown>("GET", "/v1/export");
  }
  importData(file: unknown, dryRun: boolean) {
    return this.request<ImportSummary>("POST", "/v1/import", { body: { file, mode: "merge", dryRun } });
  }

  // ---------- MCP access (owner token only) ----------
  mcpAccess() {
    return this.request<McpAccess>("GET", "/v1/mcp");
  }
  setMcpAccess(enabled: boolean) {
    return this.request<McpAccess>("PATCH", "/v1/mcp", { body: { enabled } });
  }
  rotateMcpToken() {
    return this.request<McpAccess>("POST", "/v1/mcp/rotate", { body: {} });
  }

  // ---------- Desk cloud on the Mac (owner token only) ----------
  cloudStatus() {
    return this.request<CloudStatus>("GET", "/v1/cloud");
  }
  cloudSignIn(email: string) {
    return this.request<{ ok: true }>("POST", "/v1/cloud/sign-in", { body: { email } });
  }
  cloudVerify(email: string, code: string) {
    return this.request<CloudStatus>("POST", "/v1/cloud/verify", { body: { email, code } });
  }
  cloudLink(strategy: "merge" | "download") {
    return this.request<CloudStatus>("POST", "/v1/cloud/link", { body: { strategy } });
  }
  cloudSync() {
    return this.request<CloudStatus>("POST", "/v1/cloud/sync", { body: {} });
  }
  cloudSignOut() {
    return this.request<CloudStatus>("POST", "/v1/cloud/sign-out", { body: {} });
  }

  // ---------- shared areas (cloud, or proxied by the Mac) ----------
  sharing() {
    return this.request<SharingState>("GET", "/v1/sharing");
  }
  shareArea(areaId: string, email: string) {
    return this.request<SharingState>("POST", `/v1/areas/${areaId}/share`, { body: { email } });
  }
  unshareArea(areaId: string) {
    return this.request<SharingState>("POST", `/v1/areas/${areaId}/unshare`, { body: {} });
  }
  sharingAction(hubId: string, action: "accept" | "decline" | "leave") {
    return this.request<SharingState>("POST", `/v1/sharing/${hubId}/${action}`, { body: {} });
  }
  removeMember(hubId: string, email: string) {
    return this.request<SharingState>("DELETE", `/v1/sharing/${hubId}/members/${encodeURIComponent(email)}`);
  }
}

export type TaskAction = "complete" | "reopen" | "cancel" | "unsnooze" | "review" | "delete" | "restore";
export type { Task };
